import React from 'react'
import { Button, Grid, Typography, Chip, Box, TextField } from '@material-ui/core'
import MUIDataTable from 'mui-datatables'
import { useHistory } from 'react-router-dom'
import BackupIcon from '@material-ui/icons/Backup'
import PageTitle from '../../PageTitle/PageTitle'
import { FormLoader } from '../../../Loader/FormLoader'
import { FormattedMessage, useIntl } from 'react-intl'
import Configuration from '../../../../Services/Api/Configuration'
import SubscriptionServices from '../../../../Services/Consumers/SubscriptionServices'
import { filterData } from '../../../../utils/helpers'
import { UsersFiltersPayload } from '../../../../Services/Payloads/AuthenticatedUserPayload'
import { useKeycloak } from '@react-keycloak/web'
import { useStyles } from './Styles'
import ExternalEntityGroupMenu from './ExternalEntityGroupMenu'
import { NamedRoutes } from '../../../../routes'
import ExternalEntityGroupBulkImportForm from './ExternalEntityGroupBulkImportForm'
import { ExternalEntityGroupConsumer } from '../../../../ExternalEntityGroupContext'
import Dialog from '../../Dialog'

export default function ExternalEntityGroupsContent({ handleNotificationCall, locale, externalServiceHandlers }) {
  let classes = useStyles()
  const intl = useIntl()
  const history = useHistory()
  const { keycloak } = useKeycloak()
  const [count, setCount] = React.useState(0)
  const [currentFilter, setCurrentFilter] = React.useState(null)
  const rowsPerPage = Configuration.User_Config.externalEntityGroupsLimit || 10
  const [sortOrder, setSortOrder] = React.useState({})
  const [data, setData] = React.useState(['Loading Data...'])
  const [loadedData, setLoadedData] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [canCreate, setCanCreate] = React.useState(false)
  const [reloadPage, setReloadPage] = React.useState(false)

  // Bulk import state
  const [isBulkImportDialogOpen, setIsBulkImportDialogOpen] = React.useState(false)

  // Bulk import handlers
  const handleBulkImportDialogOpen = () => {
    setIsBulkImportDialogOpen(true)
  }

  const handleBulkImportDialogClose = (clearResponse) => {
    setIsBulkImportDialogOpen(false)
    if (clearResponse) {
      clearResponse()
    }
  }

  React.useEffect(() => {
    getExternalEntityGroupsListData(1)
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  const columns = [
    {
      name: 'name',
      label: intl.formatMessage({ id: 'ExternalEntityGroups.List.Table.Name' }),
      options: {
        filterType: 'textField',
        customFilterListOptions: {
          render: (v) => {
            return (
              <div className={classes.FilterChip}>
                <FormattedMessage id='ExternalEntityGroups.List.Table.Name' />:
                <span>{v}</span>
              </div>
            )
          }
        }
      }
    },
    {
      name: 'entity_type',
      label: intl.formatMessage({ id: 'ExternalEntityGroups.List.Table.Type' }),
      options: {
        filterType: 'dropdown',
        filterOptions: {
          names: externalServiceHandlers?.map(handler => handler.key),
          renderValue: (value) => {
            const handler = externalServiceHandlers?.find(h => h.key === value)
            return handler ? handler.label : value
          }
        },
        customFilterListOptions: {
          render: (v) => {
            const handler = externalServiceHandlers.find(h => h.key === v)
            const displayValue = handler ? handler.label : v
            return (
              <div className={classes.FilterChip}>
                <FormattedMessage id='ExternalEntityGroups.List.Table.EntityType' />:
                <span>{displayValue}</span>
              </div>
            )
          }
        },
        customBodyRender: (value) => {
          const handler = externalServiceHandlers?.find(h => h.key === value)
          return handler ? handler.label : value
        }
      }
    },
    {
      name: 'references_count',
      label: intl.formatMessage({ id: 'ExternalEntityGroups.List.Table.Count' }),
      options: {
        filter: false,
        sort: false
      }
    },
    {
      name: 'references',
      label: intl.formatMessage({ id: 'ExternalEntityGroups.List.Table.References' }),
      options: {
        filter: true,
        sort: false,
        filterType: 'custom',
        customFilterListOptions: {
          render: (v) => {
            return (
              <div className={classes.FilterChip}>
                <FormattedMessage id='ExternalEntityGroups.List.Table.EntityReference' />:
                <span>{v}</span>
              </div>
            )
          }
        },
        filterOptions: {
          display: (filterList, onChange, index, column) => {
            return (
              <div className={classes.userFilterWrapper}>
                <TextField
                  label={intl.formatMessage({ id: 'ExternalEntityGroups.List.Filter.EntityReference' })}
                  fullWidth
                  value={filterList[index][0] || ''}
                  onChange={(e) => {
                    filterList[index][0] = e.target.value
                    onChange(filterList[index], index, column)
                  }}
                />
              </div>
            )
          }
        },
        customBodyRenderLite: (dataIndex) => {
          if (!data[dataIndex] || !data[dataIndex].references) return null

          const references = data[dataIndex].references
          const displayReferences = references.slice(0, 3)
          const hasMore = references.length > 3

          return (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {displayReferences.map((ref, index) => (
                <Chip
                  key={index}
                  label={ref.entity_reference}
                  size="small"
                  className={classes.userChip}
                />
              ))}
              {hasMore && (
                <Chip
                  label={`+${references.length - 3} ` + intl.formatMessage({ id: 'ExternalEntityGroups.List.Table.References.More' })}
                  size="small"
                  className={classes.moreChip}
                />
              )}
            </Box>
          )
        },
      }
    },
    {
      name: '',
      options: {
        filter: false,
        print: false,
        viewColumns: false,
        download: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          return (data[dataIndex] &&
            <ExternalEntityGroupMenu
              index={dataIndex}
              data={data}
              setData={setData}
              count={count}
              setCount={setCount}
              handleNotificationCall={handleNotificationCall} />)
        }
      }
    }
  ]

  // get data
  const getExternalEntityGroupsListData = async (page) => {
    xhrRequest(page)
  }

  // mock async function
  const xhrRequest = (page, sortOrder = {}, clear) => {
    if (!!keycloak?.authenticated) {
      setIsLoading(true)
      if (!!loadedData[page] && !!loadedData[page].length && !clear) {
        setData(loadedData[page])
        setIsLoading(false)
      } else {
        SubscriptionServices.getExternalEntityGroups({
          headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
          params: filterData(UsersFiltersPayload(page, rowsPerPage, currentFilter))
        }).then((result) => {
          if ((!!result.response && result.response.status !== 200) || !result) {
            let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
            if (result.response.data && result.response.data.status) {
              if (result.response.status === 403) {
                setData([])
                message = intl.formatMessage({ id: 'BasicInfo.AccessDeniedAlert' })
              } else if (result.response.data.status.errors && result.response.status === 422) {
                message = result.response.data.status.errors
              } else if (result.response.data.status.message) {
                message = result.response.data.status.message
              }
            }
            handleNotificationCall({
              type: 'defence',
              message: message,
              color: 'error'
            }, 'error')
          } else {
            setCanCreate(true)
            if (!!result && !!result.data && !!result.data.data) {
              let newData = result.data.data
              setSortOrder(sortOrder)
              let sortField = sortOrder.name
              let sortDir = sortOrder.direction

              if (sortField) {
                newData = newData.sort((a, b) => {
                  if (a[sortField] < b[sortField]) {
                    return 1 * (sortDir === 'asc' ? -1 : 1)
                  } else if (a[sortField] > b[sortField]) {
                    return -1 * (sortDir === 'asc' ? -1 : 1)
                  } else {
                    return 0
                  }
                })
              }
              setData(newData)
              let prevData = loadedData
              prevData[page] = newData
              setLoadedData(prevData)
              setCount(result.data.meta.total)
            }
          }
        }).then(() => {
          setIsLoading(false)
        })
      }
    }
  }

  const sort = (page, sortOrder) => {
    setSortOrder(sortOrder)
    let sortField = sortOrder.name
    let sortDir = sortOrder.direction
    let newData = data

    if (sortField) {
      newData = newData.sort((a, b) => {
        if (a[sortField] < b[sortField]) {
          return 1 * (sortDir === 'asc' ? -1 : 1)
        } else if (a[sortField] > b[sortField]) {
          return -1 * (sortDir === 'asc' ? -1 : 1)
        } else {
          return 0
        }
      })
    }
    setData(newData)
  }

  const changePage = (page, sortOrder) => {
    xhrRequest(++page, sortOrder)
  }

  const changeFilter = (applyFilters) => {
    setLoadedData([])
    let filterList = applyFilters()
    let filters = {}
    filterList.forEach((filterItem, index) => {
      if (!!filterItem.length) {
        const columnName = columns[index].name

        // Handle entity_reference filter specially
        if (columnName === 'references' && filterItem[0] && filterItem[0].length >= 1) {
          filters.entity_reference = filterItem[0]
        } else if (filterItem[0] && filterItem[0].length >= 1) {
          filters[columnName] = filterItem[0]
        }
      }
    })

    // Remove 'references' key if it exists (we use entity_reference instead)
    delete filters.references

    setCurrentFilter(filters)
  }

  React.useEffect(() => {
    if (reloadPage) {
      setLoadedData([])
      xhrRequest(1, {}, true)
      setReloadPage(false)
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [reloadPage])

  React.useEffect(() => {
    if (!!currentFilter) {
      xhrRequest(1, {}, true)
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentFilter])

  const options = {
    filter: true,
    filterType: 'dropdown',
    responsive: 'simple',
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    serverSide: true,
    count: count,
    search: false,
    rowsPerPage: rowsPerPage,
    rowsPerPageOptions: [],
    sortOrder: sortOrder,
    confirmFilters: true,
    downloadOptions: { filename: `${intl.formatMessage({ id: 'Sidebar.Links.ExternalEntityGroups' })}.csv`, separator: ',' },
    customFilterDialogFooter: (currentFilterList, applyNewFilters) => {
      return (
        <div className={classes.ApplyFilterWrapper}>
          <Button color='primary' variant="contained"
            onClick={() => changeFilter(applyNewFilters)}>
            <FormattedMessage id='Users.List.Table.ApplyFilters' /></Button>
        </div>
      )
    },
    onFilterChange: (column, filterList, type) => {
      if (type === 'chip') {
        setLoadedData([])
        let newFilters = () => (filterList)
        console.log('updating filters via chip')
        changeFilter(newFilters)
      }
    },
    textLabels: {
      body: {
        noMatch: intl.formatMessage({ id: 'ExternalEntityGroups.List.Table.NoMatching' }),
        toolTip: intl.formatMessage({ id: 'Users.List.Table.Sort' }),
        columnHeaderTooltip: column => intl.formatMessage(
          { id: 'Users.List.Table.SortFor' },
          { label: column.label })
      },
      pagination: {
        next: intl.formatMessage({ id: 'Users.List.Table.NextPage' }),
        previous: intl.formatMessage({ id: 'Users.List.Table.PreviousPage' }),
        rowsPerPage: intl.formatMessage({ id: 'Users.List.Table.RowsPerPage' }),
        displayRows: intl.formatMessage({ id: 'Users.List.Table.Of' })
      },
      toolbar: {
        search: intl.formatMessage({ id: 'Users.List.Table.Search' }),
        downloadCsv: intl.formatMessage({ id: 'Users.List.Table.Download' }),
        print: intl.formatMessage({ id: 'Users.List.Table.Print' }),
        viewColumns: intl.formatMessage({ id: 'Users.List.Table.ViewColumns' }),
        filterTable: intl.formatMessage({ id: 'Users.List.Table.FilterTable' })
      },
      filter: {
        all: intl.formatMessage({ id: 'Users.List.Table.All' }),
        title: intl.formatMessage({ id: 'Users.List.Table.FILTERS' }),
        reset: intl.formatMessage({ id: 'Users.List.Table.RESET' })
      },
      viewColumns: {
        title: intl.formatMessage({ id: 'Users.List.Table.ShowColumns' }),
        titleAria: intl.formatMessage({ id: 'Users.List.Table.ShowHideColumns' })
      }
    },
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
          changePage(tableState.page, tableState.sortOrder)
          break
        case 'sort':
          sort(tableState.page, tableState.sortOrder)
          break
        default:
          break
      }
    }
  }

  return (
    <ExternalEntityGroupConsumer>
      {({ handleBulkImportSubmit, bulkImportHasError, clearBulkImportError }) => (
        <>
          <PageTitle title={<FormattedMessage id='Sidebar.Links.ExternalEntityGroups' />}
            button={!!canCreate && (
              <Box display="flex" className={classes.buttonsContainer}>
                <Button
                  variant="outlined"
                  size="large"
                  color="primary"
                  onClick={handleBulkImportDialogOpen}
                  startIcon={<BackupIcon />}
                >
                  <FormattedMessage id='ExternalEntityGroupBulkImport.button.title' />
                </Button>
                <Button
                  classes={{ root: classes.button }}
                  variant="contained"
                  size="large"
                  color="primary"
                  onClick={() => {
                    history.push(NamedRoutes.subscription.externalEntityGroups.add)
                  }}
                >
                  <FormattedMessage id='ExternalEntityGroups.List.AddExternalEntityGroup.Title' />
                </Button>
              </Box>
            )} />
          <Grid container spacing={4}>
            <Grid item xs={12} className={classes.TableWrapper}>
              {isLoading && <FormLoader loading={true} />}
              <MUIDataTable
                title={
                  <Typography variant="h6">
                    <FormattedMessage id='ExternalEntityGroups.List.Table.Title' />
                  </Typography>
                }
                data={data}
                columns={columns}
                options={options}
              />
            </Grid>
          </Grid>

          {/* Bulk Import Dialog */}
          <Dialog
            isOpen={isBulkImportDialogOpen}
            onClose={() => handleBulkImportDialogClose(clearBulkImportError)}
            maxWidth="sm"
            fullWidth
            title={<FormattedMessage id='ExternalEntityGroupBulkImport.dialog.title' />}
          >
            <ExternalEntityGroupBulkImportForm
              onSubmit={handleBulkImportSubmit}
              cancelDialog={() => handleBulkImportDialogClose(clearBulkImportError)}
              bulkImportHasError={bulkImportHasError}
              clearBulkImportError={clearBulkImportError}
              reloadPage={setReloadPage}
            />
          </Dialog>
        </>
      )}
    </ExternalEntityGroupConsumer>
  )
}
